/**
 * Liquid Glass Navigation v4 -- Opulente
 *
 * v4 (2026-08-19)
 *  - The header-glass interceptor is now drawer-aware. While the mobile menu
 *    drawer is open the header is painted OPAQUE and its backdrop-filter is
 *    removed. Two reasons:
 *      a) backdrop-filter makes #header-component the containing block for
 *         the position:fixed .menu-drawer, so the sheet was being offset by
 *         the floating-header padding instead of anchoring to the viewport;
 *      b) it also makes the header a Backdrop Root, so any nested
 *         backdrop-filter (the dropdown panels) has nothing to sample --
 *         which is why the desktop dropdowns rendered near-invisible.
 *  - The SVG displacement / refraction overlay has been removed. The
 *    dropdown panels are opaque now (see liquid-glass-nav.css), so it was
 *    invisible, and it cost a 512x512 per-pixel canvas build on every page
 *    load. The old implementation is still in the "Main" theme if needed.
 */
(function () {
  'use strict';

  var HEADER_ID = 'header-component';

  // -- 1. HEADER GLASS INTERCEPTOR ------------------------------------
  //
  //  applyGlass() in layout/theme.liquid runs on window.load and on every
  //  header attribute mutation, writing inline !important styles that no
  //  stylesheet can override. So we intercept them at the CSSOM level:
  //
  //    - default        -> substitute the lighter frosted-glass values
  //    - drawer open    -> substitute an opaque bar and drop the blur
  //
  var LIGHT_GLASS = {
    'background':              'rgba(255, 255, 255, 0.10)',
    'backdrop-filter':         'blur(10px) saturate(140%) brightness(1.03)',
    '-webkit-backdrop-filter': 'blur(10px) saturate(140%) brightness(1.03)',
    'border':                  '1px solid rgba(255, 255, 255, 0.40)',
    'box-shadow':              'inset 0 1px 0 rgba(255,255,255,0.50), 0 0 0 0.5px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.07)',
  };

  function drawerIsOpen(header) {
    var details = header.querySelector('.menu-drawer-container');
    return !!(details && (details.hasAttribute('open') || details.classList.contains('menu-open')));
  }

  function patchHeaderGlass() {
    var el = document.getElementById(HEADER_ID);
    if (!el || el.__opteGlassPatched) return;
    el.__opteGlassPatched = true;

    var setProp    = el.style.setProperty.bind(el.style);
    var removeProp = el.style.removeProperty.bind(el.style);
    el.__opteSetProperty = setProp;

    el.style.setProperty = function (prop, value, priority) {
      if (priority === 'important') {
        if (drawerIsOpen(el)) {
          var solid = el.dataset.themeColor || '#ffffff';
          if (prop === 'background' || prop === '--color-scheme-top-row' || prop === '--color-scheme-bottom-row') {
            value = solid;
          } else if (prop === 'backdrop-filter' || prop === '-webkit-backdrop-filter') {
            return removeProp(prop);
          } else if (prop === 'border' || prop === 'box-shadow') {
            value = 'none';
          }
        } else if (Object.prototype.hasOwnProperty.call(LIGHT_GLASS, prop)) {
          value = LIGHT_GLASS[prop];
        }
      }
      return setProp(prop, value, priority);
    };
  }

  // -- 2. REPAINT ON DRAWER TOGGLE ------------------------------------
  //
  //  Opening the drawer does not touch the header's own attributes, so
  //  applyGlass() would never re-run. Writing a throwaway custom property
  //  mutates the style attribute, which trips the theme's MutationObserver
  //  and sends applyGlass() back through the interceptor above.
  //
  function watchDrawer() {
    var el = document.getElementById(HEADER_ID);
    if (!el) return;
    var details = el.querySelector('.menu-drawer-container');
    if (!details) return;

    function repaint() {
      var write = el.__opteSetProperty || el.style.setProperty.bind(el.style);
      write('--opte-header-state', drawerIsOpen(el) ? 'solid' : 'glass', 'important');
    }

    new MutationObserver(repaint).observe(details, {
      attributes: true,
      attributeFilter: ['open', 'class'],
    });

    repaint();
  }

  patchHeaderGlass(); // Runs immediately -- before window.load, where applyGlass fires

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawer);
  } else {
    watchDrawer();
  }

})();
